import { useEffect, useState } from 'react';
import { apiFetch } from '../fetchInterceptor';
import { Button } from './ui/button';
import { useWorkspace } from '../context/WorkspaceContext';
import { Sparkles, HardDrive, Zap, X, ArrowRight, ShieldCheck } from 'lucide-react';
import { cn } from '../lib/utils';
import PricingModal from './PricingModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'cb_upsell_dismissed_v1';

/**
 * Contextual upsell banner. Use `mode`:
 *  - 'documents'  → nudges paid plan when free user opens the document hub
 *  - 'storage'    → shown when storage is >80% full
 *  - 'ai_chat'    → shown when premium AI is capped
 *  - 'auto'       → picks the most relevant based on /api/billing/ai-usage payload
 */
export default function UpsellBanner({ mode = 'auto', className = '' }) {
  const { currentWorkspace } = useWorkspace();
  const [usage, setUsage] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const url = currentWorkspace?.workspace_id
          ? `${API_URL}/api/billing/ai-usage?workspace_id=${currentWorkspace.workspace_id}`
          : `${API_URL}/api/billing/ai-usage`;
        const res = await apiFetch(url);
        if (res.ok && !off) setUsage(await res.json());
      } catch (_e) { /* silent */ }
    })();
    return () => { off = true; };
  }, [currentWorkspace?.workspace_id]);

  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}');
      if (d[mode]) {
        const ageMs = Date.now() - d[mode];
        if (ageMs < 7 * 24 * 3600 * 1000) setDismissed(true);
      }
    } catch (_e) { /* iframe */ }
  }, [mode]);

  const dismiss = () => {
    setDismissed(true);
    try {
      const d = JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}');
      d[mode] = Date.now();
      localStorage.setItem(DISMISS_KEY, JSON.stringify(d));
    } catch (_e) { /* iframe */ }
  };

  if (!usage || dismissed) return null;

  const isFree = usage.plan_id === 'free';
  const storagePct = usage.storage_pct || 0;
  const aiCapped = usage.ai_status === 'capped';

  // Auto-pick the most pressing message
  let effectiveMode = mode;
  if (mode === 'auto') {
    if (aiCapped) effectiveMode = 'ai_chat';
    else if (storagePct >= 80) effectiveMode = 'storage';
    else if (isFree) effectiveMode = 'documents';
    else return null;
  }

  // Don't push docs upsell if user already paid
  if (effectiveMode === 'documents' && !isFree) return null;
  if (effectiveMode === 'storage' && storagePct < 80) return null;
  if (effectiveMode === 'ai_chat' && !aiCapped) return null;

  const CONFIG = {
    documents: {
      icon: Sparkles,
      tone: 'from-emerald-500/15 to-blue-500/15 border-emerald-500/30 text-emerald-700 dark:text-emerald-300',
      iconColor: 'text-emerald-500',
      title: 'Unlock document uploads & AI auto-categorization',
      copy: 'You\'re on the Free plan — uploads, bank sync, and AI doc extraction are locked. Upgrade to Starter for $19.99/mo and connect 1 bank, get $1.50/mo of premium AI, plus 2 GB storage. Free Llama questions stay unlimited.',
      cta: 'See plans',
    },
    storage: {
      icon: HardDrive,
      tone: 'from-amber-500/15 to-orange-500/15 border-amber-500/40 text-amber-700 dark:text-amber-300',
      iconColor: 'text-amber-500',
      title: `${storagePct.toFixed(0)}% of storage used`,
      copy: `You\'ve used ${usage.storage_used_gb} GB of your ${usage.storage_limit_gb} GB plan. Add 10 GB for just $3/mo — no plan upgrade required.`,
      cta: 'Add storage',
    },
    ai_chat: {
      icon: Zap,
      tone: 'from-violet-500/15 to-fuchsia-500/15 border-violet-500/40 text-violet-700 dark:text-violet-300',
      iconColor: 'text-violet-500',
      title: 'You\'ve hit your premium AI cap',
      copy: 'No worries — CountBucks automatically switched you to our free Llama 3.3 model so you can keep working at no extra cost. Want premium back instantly? Add $10 of Claude/GPT-4o usage for $5 one-time, or turn on auto-top-up.',
      cta: 'Buy $10 top-up',
    },
  };

  const c = CONFIG[effectiveMode];
  if (!c) return null;
  const Icon = c.icon;

  return (
    <div
      data-testid={`upsell-banner-${effectiveMode}`}
      className={cn(
        'relative overflow-hidden rounded-xl border bg-gradient-to-r p-4 flex items-start gap-3',
        c.tone,
        className,
      )}
    >
      <div className="flex-shrink-0">
        <Icon className={cn('w-5 h-5', c.iconColor)} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{c.title}</p>
        <p className="text-xs mt-1 opacity-90 leading-relaxed">{c.copy}</p>
        {/* Free-fallback reassurance — explicit user requirement */}
        {effectiveMode !== 'ai_chat' && (
          <p className="text-[11px] mt-2 inline-flex items-center gap-1.5 opacity-80">
            <ShieldCheck className="w-3 h-3" />
            Exceed your AI moat? You always drop down to the free Llama model — never blocked.
          </p>
        )}
        <div className="mt-3 flex items-center gap-2">
          <Button
            size="sm"
            className="bg-foreground text-background hover:opacity-90 gap-1.5"
            data-testid={`upsell-cta-${effectiveMode}`}
            onClick={() => setPricingOpen(true)}
          >
            {c.cta}
            <ArrowRight className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-xs"
            onClick={dismiss}
            data-testid={`upsell-dismiss-${effectiveMode}`}
          >
            Maybe later
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="flex-shrink-0 opacity-60 hover:opacity-100"
        data-testid={`upsell-close-${effectiveMode}`}
      >
        <X className="w-4 h-4" />
      </button>

      <PricingModal
        open={pricingOpen}
        onOpenChange={setPricingOpen}
        reason={effectiveMode === 'ai_chat' ? 'ai_chat' : effectiveMode === 'storage' ? 'storage' : 'documents'}
      />
    </div>
  );
}
